import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  Books,
  BookOpen,
  Check,
  X,
  Headphones,
  ArrowRight,
  Spinner,
  Trophy,
  SpeakerHigh,
  ThumbsUp,
  ThumbsDown,
  Sparkle,
  PencilLine,
  CheckCircle,
  XCircle,
  Textbox,
  GameController,
  ChatsCircle,
  CaretLeft,
  Translate,
  Pencil,
} from '@phosphor-icons/react';
import AIExerciseRunner from '../components/AIExerciseRunner';
import TeacherListeningUpload from '../components/TeacherListeningUpload';
import AITeacherPage from './AITeacherPage';
import GamesPage from './GamesPage';
import ExerciseEditModal from '../components/ExerciseEditModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExercisesPage = () => {
  const { user, demoMode } = useAuth();
  const { t, lang } = useLang();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [levelFilter, setLevelFilter] = useState(user?.spanish_level || 'all');
  const [typeFilter, setTypeFilter] = useState('');
  const [progress, setProgress] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exerciseRating, setExerciseRating] = useState(null);
  const [editingExercise, setEditingExercise] = useState(null);
  // Lazy-loaded audio for listening exercises (the list endpoint omits the
  // bytes to keep the payload light, so we fetch it on demand here too —
  // exactly the same flow the dedicated Hören page uses).
  const [audioSrc, setAudioSrc] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  // Teachers can switch to "Als Schüler ansehen" to interact with an
  // exercise exactly like a student does. Backend skips persistence for
  // teachers, so dry-runs don't pollute analytics. Default tracks the
  // platform-wide demoMode so a global Lehrer-Demo session lands on the
  // student view immediately.
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [studentMode, setStudentMode] = useState(demoMode);
  useEffect(() => { setStudentMode(demoMode); }, [demoMode]);

  // Absorbed sub-modes: chat (Conversación con IA) and games.
  const [subMode, setSubMode] = useState(null); // 'chat' | 'games' | null

  // Student AI-generate state
  const [aiLevel, setAiLevel] = useState(user?.spanish_level || 'A2');
  const [aiType, setAiType] = useState('vocabulary');
  const [aiTopic, setAiTopic] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiExercise, setAiExercise] = useState(null);
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiResult, setAiResult] = useState(null);

  const generateAIExercise = async () => {
    setAiGenerating(true);
    setAiExercise(null);
    setAiAnswer(null);
    setAiResult(null);
    try {
      const res = await axios.post(
        `${API}/exercises/student/ai-generate`,
        { level: aiLevel, exercise_type: aiType, topic: aiTopic || undefined },
        { withCredentials: true }
      );
      setAiExercise(res.data);
    } catch (e) {
      toast.error('Fehler beim Generieren');
    } finally { setAiGenerating(false); }
  };

  const submitAIAnswer = async () => {
    if (!aiExercise || aiAnswer == null) return;
    try {
      const res = await axios.post(
        `${API}/exercises/submit`,
        { exercise_id: aiExercise.exercise_id, answer: aiExercise.options[aiAnswer] },
        { withCredentials: true }
      );
      setAiResult(res.data);
      if (res.data.is_correct) {
        toast.success('Richtig! +1 Punkt');
        loadProgress();
      }
    } catch (e) {
      toast.error('Fehler beim Senden');
    }
  };

  const resetAIExercise = () => {
    setAiExercise(null); setAiAnswer(null); setAiResult(null);
  };

  useEffect(() => {
    // Reset list & only load when a type is chosen (lazy-load).
    setExercises([]);
    setSkip(0);
    setHasMore(false);
    if (typeFilter) loadExercises(0, true);
    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelFilter, typeFilter, pageSize]);

  const loadExercises = async (offset = 0, replace = false) => {
    setLoading(true);
    try {
      let url = `${API}/exercises?limit=${pageSize}&skip=${offset}`;
      if (levelFilter !== 'all') url += `&level=${levelFilter}`;
      if (typeFilter) url += `&exercise_type=${typeFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      setExercises(prev => replace ? res.data : [...prev, ...res.data]);
      setHasMore((res.data || []).length === pageSize);
      setSkip(offset + (res.data || []).length);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  };

  const loadProgress = async () => {
    try {
      const res = await axios.get(`${API}/exercises/progress/me`, { withCredentials: true });
      setProgress(res.data);
    } catch (e) { console.error(e); }
  };

  const submitAnswer = async () => {
    if (!selectedExercise || selectedAnswer === null) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/exercises/submit`, {
        exercise_id: selectedExercise.exercise_id,
        answer: selectedAnswer
      }, { withCredentials: true });
      setResult(res.data);
      loadProgress();
      // Update the card status locally so the tick/cross appears when user goes back to the list.
      setExercises(prev => prev.map(e => e.exercise_id === selectedExercise.exercise_id
        ? { ...e, last_result: res.data?.is_correct ? 'correct' : 'incorrect', last_attempted_at: new Date().toISOString() }
        : e));
    } catch (e) {
      toast.error('Fehler');
    } finally { setSubmitting(false); }
  };


  const resetExercise = () => {
    setSelectedExercise(null);
    setSelectedAnswer(null);
    setResult(null);
    setExerciseRating(null);
    setAudioSrc(null);
    setAudioLoading(false);
    setStudentMode(false);
  };

  // When a listening exercise is selected, lazily fetch its audio bytes from
  // /api/exercises/{id}/audio (which transparently reads from object storage
  // or legacy base64). Plays inline in the detail view.
  useEffect(() => {
    if (!selectedExercise || selectedExercise.exercise_type !== 'listening') {
      setAudioSrc(null);
      return;
    }
    let cancelled = false;
    setAudioLoading(true);
    setAudioSrc(null);
    axios.get(`${API}/exercises/${selectedExercise.exercise_id}/audio`, { withCredentials: true })
      .then(res => {
        if (cancelled) return;
        const b64 = res.data?.audio_base64;
        if (b64) setAudioSrc(`data:audio/mp3;base64,${b64}`);
      })
      .catch(() => { if (!cancelled) toast.error(lang === 'es' ? 'Error al cargar audio' : 'Audio konnte nicht geladen werden'); })
      .finally(() => { if (!cancelled) setAudioLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExercise?.exercise_id]);

  const rateExercise = async (exerciseId, rating) => {
    try {
      await axios.post(`${API}/exercises/${exerciseId}/rate`, { rating }, { withCredentials: true });
      setExerciseRating(rating);
      if (rating === 'down') toast.info('Danke! Der Lehrer wird das überprüfen.');
    } catch (e) { console.error(e); }
  };

  // Sub-mode early returns — Conversación con IA and Juegos are absorbed here.
  if (subMode === 'chat') {
    return (
      <div className="space-y-4" data-testid="spiele-submode-chat">
        <button
          onClick={() => setSubMode(null)}
          className="inline-flex items-center gap-1 text-sm text-[#78716C] hover:text-[#AB1C24]"
          data-testid="submode-back"
        >
          <CaretLeft size={16} /> {lang === 'es' ? 'Volver a Juegos' : 'Zurück zu Spiele'}
        </button>
        <AITeacherPage />
      </div>
    );
  }
  if (subMode === 'games') {
    return (
      <div className="space-y-4" data-testid="spiele-submode-games">
        <button
          onClick={() => setSubMode(null)}
          className="inline-flex items-center gap-1 text-sm text-[#78716C] hover:text-[#AB1C24]"
          data-testid="submode-back"
        >
          <CaretLeft size={16} /> {lang === 'es' ? 'Volver a Juegos' : 'Zurück zu Spiele'}
        </button>
        <GamesPage />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exercises-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#6D28D9]/10 flex items-center justify-center flex-shrink-0">
            <GameController size={28} weight="duotone" className="text-[#6D28D9]" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524]">
              {lang === 'es' ? 'Juegos' : 'Spiele'}
            </h1>
            <p className="text-sm text-[#57534E] mt-0.5">
              {lang === 'es'
                ? 'Conversación IA, ejercicios y mini-juegos — todo en un solo sitio.'
                : 'KI-Chat, Übungen und Mini-Spiele — alles an einem Ort.'}
            </p>
          </div>
        </div>
        {progress && (
          <div className="flex items-center gap-4 bg-white rounded-xl border border-[#D6D3D1] px-4 py-3 flex-shrink-0">
            <div className="text-center">
              <span className="block text-lg font-bold text-[#292524]">{progress.total_completed}</span>
              <span className="text-xs text-[#78716C]">{lang === 'es' ? 'Hechos' : 'Gemacht'}</span>
            </div>
            <div className="w-px h-8 bg-[#D6D3D1]" />
            <div className="text-center">
              <span className="block text-lg font-bold text-[#6B8E23]">{progress.accuracy}%</span>
              <span className="text-xs text-[#78716C]">{lang === 'es' ? 'Acierto' : 'Richtig'}</span>
            </div>
          </div>
        )}
      </div>

      {/* KI-Übungen — AI generator for all supported types + teacher audio upload for listening.
          Only shown when the user has explicitly selected a type, integrating the former
          KI-Übungen flow directly into Interaktive Übungen. */}
      {['vocabulary', 'grammar', 'translation', 'listening', 'reading'].includes(typeFilter) && (
      <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5 space-y-3" data-testid="ai-generator-section">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#D97706]/10 flex items-center justify-center flex-shrink-0">
            <Sparkle size={26} weight="duotone" className="text-[#D97706]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-heading font-semibold text-[#292524]">
              {lang === 'es' ? 'Ejercicios con IA' : 'KI-Übungen'}
            </h2>
            <p className="text-sm text-[#78716C]">
              {lang === 'es'
                ? 'Genera un ejercicio a medida sobre este tema.'
                : 'Lass dir eine Übung nach Maß erzeugen.'}
            </p>
          </div>
        </div>
        <AIExerciseRunner user={user} typeKey={typeFilter} showHeader={false} />

        {/* Teacher upload composer — only for listening (audio files). */}
        {typeFilter === 'listening' && (user?.role === 'admin' || user?.role === 'teacher') && (
          <TeacherListeningUpload />
        )}
      </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap items-center">
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-[150px]" data-testid="exercise-level-filter">
            <SelectValue placeholder="Niveau" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Niveaus</SelectItem>
            <SelectItem value="A1">A1</SelectItem>
            <SelectItem value="A2">A2</SelectItem>
            <SelectItem value="B1">B1</SelectItem>
            <SelectItem value="B2">B2</SelectItem>
          </SelectContent>
        </Select>
        {typeFilter && (
          <>
            <Select value={String(pageSize)} onValueChange={v => setPageSize(parseInt(v, 10))}>
              <SelectTrigger className="w-[140px]" data-testid="exercise-pagesize-filter">
                <SelectValue placeholder="Anzeigen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 Übungen</SelectItem>
                <SelectItem value="50">50 Übungen</SelectItem>
                <SelectItem value="100">100 Übungen</SelectItem>
                <SelectItem value="200">200 Übungen</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => setTypeFilter('')}
              className="text-xs text-[#78716C] hover:text-[#AB1C24] underline"
              data-testid="reset-type-filter"
            >
              {t('ex.change_type')}
            </button>
          </>
        )}
      </div>

      {/* Active Exercise Detail */}
      {selectedExercise && (
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="exercise-detail">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap" data-testid="exercise-detail-levels">
              {((selectedExercise.levels && selectedExercise.levels.length > 0) ? selectedExercise.levels : [selectedExercise.level]).map(lvl => (
                <span key={lvl} className={`level-badge ${lvl}`}>{lvl}</span>
              ))}
              <span className="text-sm text-[#78716C] capitalize">{selectedExercise.exercise_type}</span>
            </div>
            <div className="flex items-center gap-2">
              {isTeacher && (
                <button
                  onClick={() => { setStudentMode(s => !s); setSelectedAnswer(null); setResult(null); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    studentMode
                      ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                      : 'bg-white text-[#292524] border-stone-300 hover:bg-stone-50'
                  }`}
                  title={lang === 'es' ? 'Cambia entre la vista del profesor y la del alumno' : 'Schaltet zwischen Lehrer-Vorschau und echter Schüleransicht um'}
                  data-testid="exercise-student-mode-toggle"
                >
                  <span className={`inline-block w-2 h-2 rounded-full ${studentMode ? 'bg-white' : 'bg-emerald-500'}`} />
                  {studentMode
                    ? (lang === 'es' ? 'Vista de alumno activa' : 'Schüleransicht aktiv')
                    : (lang === 'es' ? 'Ver como alumno' : 'Als Schüler ansehen')}
                </button>
              )}
              <button onClick={resetExercise} className="text-[#78716C] hover:text-[#292524]">
                <X size={20} />
              </button>
            </div>
          </div>

          {isTeacher && !studentMode && (
            <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              {lang === 'es' ? 'Vista previa (solución marcada en verde)' : 'Vorschau (Lösung grün hervorgehoben)'}
            </p>
          )}

          <h3 className="font-heading font-semibold text-lg text-[#292524] mb-2">{selectedExercise.title}</h3>
          <p className="text-[#57534E] mb-6">{selectedExercise.question}</p>

          {/* Audio player for listening exercises — lazily loaded above. */}
          {selectedExercise.exercise_type === 'listening' && (
            <div className="mb-6" data-testid={`exercise-audio-${selectedExercise.exercise_id}`}>
              {audioLoading && (
                <div className="h-10 flex items-center text-xs text-[#A8A29E]">
                  <Spinner size={14} className="animate-spin mr-2" />
                  {lang === 'es' ? 'Cargando audio…' : 'Lade Audio…'}
                </div>
              )}
              {!audioLoading && audioSrc && (
                <audio controls preload="metadata" src={audioSrc} className="w-full max-w-md h-9 rounded-full" style={{maxHeight:'36px'}} />
              )}
              {!audioLoading && !audioSrc && (
                <p className="text-xs text-[#A8A29E]">
                  {lang === 'es' ? 'No hay audio disponible para este ejercicio.' : 'Kein Audio für diese Übung verfügbar.'}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {selectedExercise.options?.map((opt, i) => {
              // Teacher preview (default for teachers): pre-mark the correct
              // option in green with a "Lösung" badge so the answer is obvious
              // at a glance, no need to submit.
              const teacherPreview = isTeacher && !studentMode;
              const isCorrectAnswer = teacherPreview && selectedExercise.correct_answer === opt;
              return (
                <button
                  key={i}
                  onClick={() => !result && !teacherPreview && setSelectedAnswer(opt)}
                  disabled={!!result || teacherPreview}
                  className={`p-4 rounded-xl text-left font-medium transition-all border-2 ${
                    teacherPreview
                      ? isCorrectAnswer
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-900'
                        : 'bg-white border-[#D6D3D1] text-[#292524] opacity-80 cursor-default'
                      : result
                        ? opt === result.correct_answer
                          ? 'bg-green-50 border-green-400 text-green-800'
                          : opt === selectedAnswer
                            ? 'bg-red-50 border-red-400 text-red-800'
                            : 'bg-gray-50 border-gray-200 text-[#78716C]'
                        : selectedAnswer === opt
                          ? 'bg-[#FDF0ED] border-[#AB1C24] text-[#AB1C24]'
                          : 'bg-white border-[#D6D3D1] text-[#292524] hover:border-[#AB1C24]/50'
                  }`}
                  data-testid={`exercise-option-${i}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span>{opt}</span>
                    {isCorrectAnswer && (
                      <span className="text-[10px] uppercase font-bold tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded">
                        {lang === 'es' ? 'Solución' : 'Lösung'}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {!result && (!isTeacher || studentMode) ? (
            <Button
              onClick={submitAnswer}
              disabled={selectedAnswer === null || submitting}
              className="w-full bg-[#AB1C24] hover:bg-[#8A151C]"
              data-testid="submit-exercise-btn"
            >
              {submitting ? 'Prüfe...' : 'Antwort prüfen'}
            </Button>
          ) : result ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                <div className={`flex items-center gap-2 ${result.is_correct ? 'text-green-700' : 'text-red-700'}`}>
                  {result.is_correct ? <Check size={24} weight="bold" /> : <X size={24} weight="bold" />}
                  <span className="font-semibold">{result.is_correct ? 'Richtig!' : 'Falsch'}</span>
                  {result.is_correct && (result.points_earned ?? 0) > 0 && <span className="text-sm ml-2">+{result.points_earned} Punkte</span>}
                  {result.is_correct && (result.points_earned ?? 0) === 0 && isTeacher && (
                    <span className="text-[10px] uppercase tracking-wider ml-2 bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded">
                      {lang === 'es' ? 'Modo profesor' : 'Lehrer-Modus'}
                    </span>
                  )}
                </div>
                <Button variant="outline" onClick={resetExercise} data-testid="next-exercise-btn">
                  Nächste Übung <ArrowRight size={16} className="ml-1" />
                </Button>
              </div>
              <div className="flex items-center justify-center gap-4 text-sm text-[#78716C]">
                <span>War diese Übung gut?</span>
                <button onClick={() => rateExercise(selectedExercise.exercise_id, 'up')} className={`p-2 rounded-lg transition-colors ${exerciseRating === 'up' ? 'bg-green-100 text-green-600' : 'hover:bg-green-50 hover:text-green-600'}`} data-testid="rate-up-exercise">
                  <ThumbsUp size={20} weight={exerciseRating === 'up' ? 'fill' : 'regular'} />
                </button>
                <button onClick={() => rateExercise(selectedExercise.exercise_id, 'down')} className={`p-2 rounded-lg transition-colors ${exerciseRating === 'down' ? 'bg-red-100 text-red-600' : 'hover:bg-red-50 hover:text-red-600'}`} data-testid="rate-down-exercise">
                  <ThumbsDown size={20} weight={exerciseRating === 'down' ? 'fill' : 'regular'} />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Type picker (lazy-load) — big card grid, unified "Spiele" hub.
          Includes Conversación con IA (chat) and Juegos (mini-games) as sub-modes. */}
      {!selectedExercise && !typeFilter && (
        <div data-testid="type-picker">
          <div className="mb-4">
            <h2 className="font-heading font-semibold text-xl text-[#292524]">
              {lang === 'es' ? 'Elige una actividad' : 'Wähle eine Aktivität'}
            </h2>
            <p className="text-sm text-[#78716C] mt-1">
              {lang === 'es'
                ? 'Chatea con la IA, genera ejercicios o juega mini-juegos.'
                : 'Chatte mit der KI, erzeuge Übungen oder spiele Mini-Spiele.'}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[
              { v: '__chat__',         isSub: true,   icon: ChatsCircle, color: '#AB1C24', label: { de: 'Gespräch mit KI',  es: 'Conversación con IA' }, desc: { de: 'Chatte frei mit der KI-Lehrerin',  es: 'Chatea libremente con la Profesora IA' } },
              { v: 'vocabulary',       icon: Books,        color: '#5B8C9A', label: { de: 'Vokabeln',        es: 'Vocabulario' },           desc: { de: 'Neue Wörter im Kontext',        es: 'Palabras nuevas en contexto' } },
              { v: 'grammar',          icon: PencilLine,   color: '#8B5A3C', label: { de: 'Grammatik',       es: 'Gramática' },             desc: { de: 'Konjugationen und Strukturen',  es: 'Conjugaciones y estructuras' } },
              { v: 'translation',      icon: Translate,    color: '#2F6B50', label: { de: 'Übersetzung',     es: 'Traducción' },            desc: { de: 'DE↔ES Mini-Übungen',            es: 'Mini-ejercicios DE↔ES' } },
              { v: 'listening',        icon: Headphones,   color: '#D97706', label: { de: 'Hörverstehen',    es: 'Comprensión auditiva' },  desc: { de: 'Übungen mit Audio',             es: 'Ejercicios con audio' } },
              { v: 'reading',          icon: BookOpen,     color: '#E06D53', label: { de: 'Leseverstehen',   es: 'Comprensión lectora' },   desc: { de: 'Kurze Texte mit Fragen',        es: 'Textos cortos con preguntas' } },
              { v: '__games__',        isSub: true,   icon: GameController, color: '#6D28D9', label: { de: 'Mini-Spiele',  es: 'Mini-juegos' },           desc: { de: 'Lernen durch Spielen',          es: 'Aprende jugando' } },
            ].map(({ v, icon: Ic, color, label, desc, isSub }) => (
              <button
                key={v}
                onClick={() => {
                  if (v === '__chat__') setSubMode('chat');
                  else if (v === '__games__') setSubMode('games');
                  else setTypeFilter(v);
                }}
                data-testid={`pick-type-${v.replace(/__/g, '')}`}
                className="group bg-white rounded-2xl border border-stone-200 hover:shadow-xl hover:-translate-y-0.5 transition-all text-left p-5 sm:p-6 flex flex-col gap-3 min-h-[160px] sm:min-h-[200px]"
                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
              >
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform"
                  style={{ backgroundColor: `${color}15` }}
                >
                  <Ic size={32} weight="duotone" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-heading font-semibold text-[#292524] leading-tight break-words">
                    {label[lang] || label.de}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#78716C] mt-1 leading-snug">
                    {desc[lang] || desc.de}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Exercises Grid */}
      {!selectedExercise && typeFilter && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading && exercises.length === 0 ? (
              <div className="col-span-full flex justify-center py-12">
                <Spinner size={32} className="animate-spin text-[#AB1C24]" />
              </div>
            ) : exercises.length > 0 ? (
              exercises.map((ex) => {
                const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
                return (
                <div
                  key={ex.exercise_id}
                  className={`relative bg-white rounded-xl border p-5 hover:shadow-md transition-shadow group ${
                    ex.last_result === 'correct' ? 'border-emerald-200' : ex.last_result === 'incorrect' ? 'border-rose-200' : 'border-[#D6D3D1]'
                  }`}
                  data-testid={`exercise-card-${ex.exercise_id}`}
                >
                  {ex.last_result && (
                    <span
                      className={`absolute top-3 right-3 inline-flex items-center justify-center w-7 h-7 rounded-full ${
                        ex.last_result === 'correct' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                      }`}
                      data-testid={`exercise-status-${ex.exercise_id}`}
                      title={ex.last_result === 'correct' ? t('ex.last_correct') : t('ex.last_incorrect')}
                    >
                      {ex.last_result === 'correct'
                        ? <CheckCircle size={18} weight="fill" />
                        : <XCircle size={18} weight="fill" />}
                    </span>
                  )}
                  {isTeacher && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingExercise(ex); }}
                      className="absolute top-3 left-3 p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 z-10"
                      title="Übung bearbeiten"
                      data-testid={`exercise-edit-${ex.exercise_id}`}
                    >
                      <Pencil size={13} weight="bold" />
                    </button>
                  )}
                  <button
                    onClick={() => { setSelectedExercise(ex); setSelectedAnswer(null); setResult(null); }}
                    className="block w-full text-left"
                    data-testid={`exercise-open-${ex.exercise_id}`}
                  >
                    <div className={`flex items-center justify-between mb-3 ${isTeacher ? 'pl-9' : ''} pr-9`}>
                      <div className="flex flex-wrap gap-1" data-testid={`exercise-card-levels-${ex.exercise_id}`}>
                        {((ex.levels && ex.levels.length > 0) ? ex.levels : [ex.level]).map(lvl => (
                          <span key={lvl} className={`level-badge ${lvl}`}>{lvl}</span>
                        ))}
                      </div>
                      <span className="text-xs text-[#78716C] capitalize bg-[#F5F5F4] px-2 py-1 rounded-full">{ex.exercise_type}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-[#292524] mb-1 group-hover:text-[#AB1C24] transition-colors">{ex.title}</h3>
                    <p className="text-sm text-[#78716C] line-clamp-2">{ex.question}</p>
                  </button>
                </div>
              );})
            ) : (
              <div className="col-span-full text-center py-12">
                <Books size={48} className="mx-auto text-[#78716C] mb-4" />
                <p className="text-[#78716C]">{t('ex.none_found')}</p>
              </div>
            )}
          </div>
          {hasMore && (
            <div className="flex justify-center">
              <Button
                onClick={() => loadExercises(skip)}
                disabled={loading}
                variant="outline"
                data-testid="load-more-exercises"
              >
                {loading ? t('btn.loading') : t('ex.load_more')}
              </Button>
            </div>
          )}
        </div>
      )}
      <ExerciseEditModal
        exercise={editingExercise}
        open={!!editingExercise}
        onClose={() => setEditingExercise(null)}
        onSaved={(updated) => {
          setExercises(prev => prev.map(e => e.exercise_id === updated.exercise_id ? { ...e, ...updated } : e));
          setEditingExercise(null);
        }}
      />
    </div>
  );
};

export default ExercisesPage;
